import { clsx } from "clsx";
import { type CSSProperties, type HTMLAttributes, useRef } from "react";
import { mergeProps } from "react-aria";

import { useMeasureOnResize } from "../../hooks/useMeasureOnResize.ts";
import { useTooltip } from "../../hooks/useTooltip.ts";
import { truncateByOverflow } from "../../utils/string/truncate-middle.ts";
import { AnimatedTooltip } from "../Tooltip/AnimatedTooltip.tsx";
import css from "./Truncate.module.css";

interface TruncateTextProps {
  text: string;
  ellipsis?: string;
  className?: string;
  style?: CSSProperties;
}

export function TruncateText({
  text,
  ellipsis = "…",
  className = "",
  style,
  ...props
}: TruncateTextProps & HTMLAttributes<HTMLSpanElement>) {
  const elementRef = useRef<HTMLSpanElement>(null);
  const measureText = (element: HTMLSpanElement, shouldCancel: () => boolean) =>
    truncateByOverflow(element, text, ellipsis, shouldCancel);
  const displayText = useMeasureOnResize(elementRef, measureText, [text, ellipsis], text);
  const isDisabled = displayText === text;
  const { state, tooltipProps, triggerProps } = useTooltip(elementRef, { isDisabled });

  return (
    <>
      <span
        ref={elementRef}
        className={clsx(css.text, className)}
        style={style}
        data-testid="truncate-text"
        {...mergeProps(props, triggerProps)}
        aria-label={text}
      >
        {displayText}
      </span>
      <AnimatedTooltip
        domProps={tooltipProps}
        state={state}
        targetRef={elementRef}
        title={text}
        nonInteractive
        offset={5}
        style={{ maxWidth: "unset" }}
      />
    </>
  );
}
